import { MoneyText } from '@/components/MoneyText';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { NetWorth, NetWorthSeriesPoint } from '@/api/netWorth';
import { formatCivilDate, formatCivilDateNear, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useOptionalMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { TEXT_LABEL } from '@/theme/textLabel';
import { OpeningPendingNote } from '@/features/accounts/OpeningPendingNote';

const WIDTH = 240;
const HEIGHT = 56;
const PAD = { top: 6, right: 8, bottom: 6, left: 8 };

interface NetWorthCardProps {
  netWorth: NetWorth;
  currency: string | null;
  /**
   * The accounts the page's totals count from 0 (TMMAPP-0123 item 19a): net worth's own list, merged
   * by My Accounts with `/me/summary`'s. Defaults to net worth's.
   */
  openingPendingAccountIds?: readonly number[];
  /**
   * `headline` (TMMAPP-0151 §D 10): My Accounts' v2 header figure — "Net worth" over the figure in
   * `figureHero`, with the same notes under it, on the page canvas (no card); no sparkline
   * (TMMAPP-0156 item 5).
   */
  variant?: 'card' | 'headline';
}

function sparklinePoints(series: NetWorthSeriesPoint[]): { x: number; y: number }[] {
  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const values = series.map((p) => p.netWorthMinor);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  return series.map((point, index) => {
    const t = series.length === 1 ? 0 : index / (series.length - 1);
    const x = PAD.left + t * plotW;
    const y =
      span === 0 ? PAD.top + plotH / 2 : PAD.top + ((max - point.netWorthMinor) / span) * plotH;
    return { x, y };
  });
}

/**
 * The net-worth card in My Accounts' header row, top right (TMMAPP-0102, visual contract §4,
 * P:532–538): `surface` at `radius.card` with the raised elevation, a 3px `brandBoundary` top rule,
 * padding 14 22 16, min-width 232. The label is 12px .09em uppercase; the figure is `figureXl`
 * (40/300) in `link` (a negative one stays `moneyNegative`); the note is "Assets less
 * liabilities · {asOf}". The sparkline and the valued / unmanaged captions stay under the note
 * (D1): a 12-point inline SVG on `tokens.brandBoundary` (no chart library).
 */
export function NetWorthCard({
  netWorth,
  currency,
  openingPendingAccountIds = netWorth.openingPendingAccountIds,
  variant = 'card',
}: NetWorthCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const zone = useOptionalMe()?.user.timezone ?? 'UTC';
  const tokens = resolveTokens(useTheme());
  const series = netWorth.series;
  const points = series.length > 0 ? sparklinePoints(series) : [];
  const last = points[points.length - 1];
  const historyLabel = (point: NetWorthSeriesPoint | undefined) =>
    point === undefined
      ? t('common.notAvailable')
      : formatMoneyMinor(point.netWorthMinor, currency, locale);
  const firstFigure = historyLabel(series[0]);
  const lastFigure = historyLabel(series[series.length - 1]);
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const negative = netWorth.netWorthMinor < 0;
  const headline = variant === 'headline';
  const canvas = headline ? 'bg' : 'surface';

  return (
    <Paper
      elevation={headline ? 0 : 2}
      data-net-worth-card={headline ? 'headline' : ''}
      sx={{
        minWidth: { sm: headline ? 0 : 232 },
        maxWidth: '100%',
        p: headline ? 0 : '14px 22px 16px',
        textAlign: 'start',
        ...(headline
          ? { bgcolor: 'transparent', boxShadow: 'none', [TEXT_LABEL]: tokens.textLabel('bg') }
          : { borderTop: `3px solid ${tokens.brandBoundary}` }),
      }}
    >
      {headline ? (
        <Typography
          component="div"
          sx={{ fontSize: 12, lineHeight: '17px', color: tokens.textLabel('bg') }}
        >
          {t('networth.title')}
        </Typography>
      ) : (
        <Typography variant="eyebrow" component="div">
          {t('networth.title')}
        </Typography>
      )}
      <Box
        sx={
          negative
            ? undefined
            : { '& .MuiTypography-root': { color: tokens.link, textAlign: 'start' } }
        }
      >
        <MoneyText
          minor={netWorth.netWorthMinor}
          currency={currency}
          whole
          variant={headline ? 'figureHero' : 'figureXl'}
          component="div"
        />
      </Box>
      <Typography
        variant="caption"
        component="div"
        sx={{ mt: '4px', color: tokens.textLabel(canvas) }}
      >
        {/* This year's date without the year (TMMAPP-0157 item 4; visual contract v2 §13). */}
        {netWorth.asOf !== null
          ? t('networth.noteAsOf', {
              date: formatCivilDateNear(netWorth.asOf, locale, todayCivilInZone(zone)),
            })
          : t('networth.note')}
      </Typography>
      {netWorth.valuedAsOf !== null && (
        <Typography variant="caption" component="div" sx={{ color: tokens.textLabel(canvas) }}>
          {t('networth.valuedAsOf', { date: formatCivilDate(netWorth.valuedAsOf, locale) })}
        </Typography>
      )}
      {netWorth.unmanagedIncluded && (
        <Typography variant="caption" component="div" sx={{ color: tokens.textLabel(canvas) }}>
          {t('networth.unmanagedIncluded')}
        </Typography>
      )}
      <OpeningPendingNote count={openingPendingAccountIds.length} />
      {/* The headline is "Net worth" and the figure alone (TMMAPP-0156 item 5): the trend drew a
          stray line across My Accounts' header, and the prototype has none there. */}
      {!headline && points.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <svg
            role="img"
            aria-label={t('networth.sparklineLabel', { first: firstFigure, last: lastFigure })}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            width="100%"
            height={HEIGHT}
            style={{ display: 'block' }}
            data-point-count={series.length}
          >
            <rect width={WIDTH} height={HEIGHT} fill={tokens.surface} />
            {path.length > 0 && (
              <path d={path} fill="none" stroke={tokens.brandBoundary} strokeWidth={2} />
            )}
            {last !== undefined && (
              <circle cx={last.x} cy={last.y} r={3.5} fill={tokens.brandBoundary} />
            )}
          </svg>
        </Box>
      )}
    </Paper>
  );
}
